import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Gauge,
  Loader2,
  Mail,
  MousePointerClick,
  Plus,
  Send,
  ShieldCheck,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { cn } from '../lib/cn';
import { MailingIntestazione } from '../components/email/MailingIntestazione';
import { useNotifications } from '../store/useStore';
import { listeService } from '../lib/email/listeService';
import { descriviPubblico } from '../lib/email/pubblico';
import { campaignService } from '../lib/email/emailService';
import { EMAIL_CAMPAIGN_STATUS, type EmailCampaign, type EmailSaluteLista } from '../types/email.types';

function dataIt(valore: string | null): string {
  if (!valore) return '—';
  return new Date(valore).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function percentuale(parte: number, totale: number): string {
  if (!totale) return '—';
  return `${Math.round((parte / totale) * 100)}%`;
}

export default function EmailCampaignsPage() {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  const [campagne, setCampagne] = useState<EmailCampaign[]>([]);
  const [quota, setQuota] = useState(0);
  const [salute, setSalute] = useState<EmailSaluteLista | null>(null);
  const [modalita, setModalita] = useState<'sandbox' | 'reale'>('sandbox');
  const [loading, setLoading] = useState(true);

  const [nuovaAperta, setNuovaAperta] = useState(false);
  const [nomeNuova, setNomeNuova] = useState('');
  const [creando, setCreando] = useState(false);
  const [nomiListe, setNomiListe] = useState<Record<string, string>>({});

  const carica = useCallback(
    async (inSecondoPiano = false) => {
      if (!inSecondoPiano) setLoading(true);
      try {
        const [elenco, q, s, m, liste] = await Promise.all([
          campaignService.elenco(),
          campaignService.quotaResidua(),
          campaignService.saluteLista(),
          campaignService.modalita(),
          listeService.elenco(),
        ]);
        setCampagne(elenco);
        setNomiListe(Object.fromEntries(liste.map((l) => [l.id, l.name])));
        setQuota(q);
        setSalute(s);
        setModalita(m);
      } catch (errore) {
        addNotification({
          type: 'error',
          title: 'Non riesco a caricare le campagne',
          message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
        });
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  useEffect(() => {
    carica();
  }, [carica]);

  const crea = useCallback(async () => {
    if (!nomeNuova.trim()) return;
    setCreando(true);
    try {
      const campagna = await campaignService.crea(nomeNuova.trim());
      navigate(`/mailing/campagne/${campagna.id}`);
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Non riesco a creare la campagna',
        message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
      });
    } finally {
      setCreando(false);
    }
  }, [nomeNuova, navigate, addNotification]);

  const kpi = useMemo(
    () => [
      { label: 'Campagne', value: campagne.length, icon: Mail, color: 'text-primary-500' },
      {
        label: 'Inviabili oggi',
        value: quota,
        icon: Gauge,
        color: quota > 0 ? 'text-green-500' : 'text-orange-500',
      },
      {
        label: 'Rimbalzi 30gg',
        value: salute?.perc_rimbalzi != null ? `${salute.perc_rimbalzi}%` : '—',
        icon: ShieldCheck,
        color: (salute?.perc_rimbalzi ?? 0) > 5 ? 'text-red-500' : 'text-green-500',
      },
      {
        label: 'Reclami 30gg',
        value: salute?.perc_reclami != null ? `${salute.perc_reclami}%` : '—',
        icon: AlertTriangle,
        color: (salute?.perc_reclami ?? 0) > 0.3 ? 'text-red-500' : 'text-green-500',
      },
    ],
    [campagne.length, quota, salute]
  );

  if (loading) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="h-10 w-full max-w-md rounded-lg bg-gray-100 animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="h-20 md:h-24 animate-pulse bg-gray-50 border-gray-100" />
          ))}
        </div>
        <div className="grid gap-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="h-24 animate-pulse bg-gray-50 border-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <MailingIntestazione descrizione="Newsletter e comunicazioni che partono una volta sola verso una lista di contatti.">
        <Button
          onClick={() => setNuovaAperta(true)}
          className="h-10 px-4 shadow-lg shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuova campagna
        </Button>
      </MailingIntestazione>

      {modalita === 'sandbox' && (
        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <strong>Modalita' di prova attiva.</strong> Le campagne si compongono e si possono
            leggere per intero, ma non esce niente davvero. Si passa all'invio reale dalle
            impostazioni del modulo, dopo aver sistemato il dominio.
          </div>
        </div>
      )}

      {(salute?.perc_reclami ?? 0) > 0.3 && (
        <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-red-900 leading-relaxed">
            <strong>Troppe segnalazioni di spam ({salute?.perc_reclami}%).</strong> Sopra lo 0,3%
            Gmail comincia a rifiutare i messaggi, comprese le comunicazioni di servizio. Conviene
            fermarsi e rivedere a chi si sta scrivendo.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {kpi.map((voce) => (
          <Card key={voce.label} className="border-gray-200 bg-white">
            <CardContent className="p-3 md:p-6">
              <div className="flex items-center justify-between mb-2 gap-2">
                <p className="text-[10px] md:text-xs font-medium text-neutral-500 uppercase tracking-wider truncate">
                  {voce.label}
                </p>
                <voce.icon className={cn('h-3 w-3 md:h-4 md:w-4 flex-shrink-0', voce.color)} />
              </div>
              <div className="text-xl md:text-2xl font-bold text-gray-900">{voce.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="space-y-3">
        {campagne.length === 0 ? (
          <div className="text-center py-14 rounded-xl border border-dashed border-gray-200 bg-white">
            <Send className="w-12 h-12 text-neutral-400 mx-auto mb-4 opacity-30" />
            <p className="text-neutral-500">Nessuna campagna</p>
            <p className="text-xs text-neutral-400 mt-2 max-w-md mx-auto">
              Una campagna e' una comunicazione che parte una volta sola verso una lista di
              contatti.
            </p>
            <Button className="mt-4" onClick={() => setNuovaAperta(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Crea la prima campagna
            </Button>
          </div>
        ) : (
          campagne.map((c) => {
            const stato = EMAIL_CAMPAIGN_STATUS[c.status];
            const partite = c.delivered_count + c.bounce_count;
            return (
              <Card
                key={c.id}
                onClick={() => navigate(`/mailing/campagne/${c.id}`)}
                className="group relative overflow-hidden p-4 cursor-pointer bg-white border-gray-200 hover:border-primary-500 hover:shadow-xl transition-all duration-300"
              >
                <div
                  className={cn(
                    'absolute left-0 top-0 h-full w-1 opacity-70 group-hover:opacity-100 transition-opacity',
                    stato.accento
                  )}
                />
                <div className="flex flex-col gap-3 pl-2 md:flex-row md:items-center md:justify-between md:gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-bold text-base text-gray-900 group-hover:text-primary-600 transition-colors break-words">
                        {c.name}
                      </h3>
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border',
                          stato.badge
                        )}
                        title={stato.spiegazione}
                      >
                        {stato.label}
                      </span>
                    </div>
                    <p className="text-xs text-neutral-500 mt-1 truncate">
                      {c.subject || 'Oggetto non ancora scritto'}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
                      <span>
                        {descriviPubblico({ pubblico: c.pubblico, liste: c.tag_filter ?? [] }, nomiListe)}
                        {c.pubblico !== 'da_scegliere' && ` · ${c.target_count} destinatari`}
                      </span>
                      {c.scheduled_at && <span>Partenza {dataIt(c.scheduled_at)}</span>}
                      {partite > 0 && (
                        <>
                          <span className="inline-flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5 text-neutral-400" />
                            {c.delivered_count} consegnate
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <MousePointerClick className="w-3.5 h-3.5 text-neutral-400" />
                            {percentuale(c.click_count, c.delivered_count)} clic
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      <Dialog open={nuovaAperta} onOpenChange={(v) => !v && setNuovaAperta(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuova campagna</DialogTitle>
            <DialogDescription>
              Come la chiami? E' un nome per te, i destinatari non lo vedono.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={nomeNuova}
            onChange={(e) => setNomeNuova(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && crea()}
            placeholder="Es. Newsletter primavera 2026"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNuovaAperta(false)}>
              Annulla
            </Button>
            <Button
              onClick={crea}
              disabled={!nomeNuova.trim() || creando}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              {creando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Crea e componi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
